/**
 * BundleAttachmentPicker.js
 *
 * Collapsible "Attach more documents & files" expander shown inside email
 * compose dialogs.  Fetches all available attachments for a given owner + sale
 * and renders grouped checkboxes.
 *
 * Props:
 *   saleId       — used to scope horse deliverables
 *   ownerId      — the owner's CRM entity ID
 *   excludeFileIds — file IDs already attached as the primary (hide them)
 *   onChange       — (selectedFileIds: string[]) => void
 */
import React, { useState, useEffect } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../ui/collapsible';
import { Checkbox } from '../ui/checkbox';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { ChevronDown, ChevronRight, Paperclip, Loader2, Eye } from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';

const GROUP_LABELS = {
  owner_deliverables: 'Owner Deliverables',
  horse_deliverables: 'Horse Deliverables',
  horse_files:        'Horse Files & Photos',
  owner_files:        'Owner Documents',
};

const BundleAttachmentPicker = ({
  saleId,
  ownerId,
  excludeFileIds = [],
  onChange,
}) => {
  const [open, setOpen]           = useState(false);
  const [loading, setLoading]     = useState(false);
  const [groups, setGroups]       = useState(null);
  const [selected, setSelected]   = useState({});   // { fileId: true }
  const excludeSet                = new Set(excludeFileIds.filter(Boolean));

  const openPreview = (fileId) => {
    const token = localStorage.getItem('token');
    fetch(`${window.location.origin}/api/files/download/${fileId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.blob())
      .then(blob => window.open(URL.createObjectURL(blob), '_blank'));
  };

  useEffect(() => {
    if (open && groups === null && saleId && ownerId) {
      setLoading(true);
      api.get(`/sale-owner-deliverables/available-attachments?sale_id=${saleId}&owner_id=${ownerId}`)
        .then(r => setGroups(r.data || {}))
        .catch(() => setGroups({}))
        .finally(() => setLoading(false));
    }
  }, [open, saleId, ownerId]); // eslint-disable-line

  const toggle = (fileId) => {
    setSelected(prev => {
      const next = { ...prev, [fileId]: !prev[fileId] };
      const ids = Object.entries(next).filter(([, v]) => v).map(([k]) => k);
      onChange?.(ids);
      return next;
    });
  };

  const totalAvailable = groups
    ? Object.values(groups).flat().filter(i => !excludeSet.has(i.file_id)).length
    : 0;

  const selectedCount = Object.values(selected).filter(Boolean).length;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors w-full text-left py-1">
        {open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />}
        <Paperclip className="w-3.5 h-3.5 shrink-0" />
        <span className="font-medium">Attach more documents &amp; files</span>
        {selectedCount > 0 && (
          <Badge variant="secondary" className="ml-auto text-xs">{selectedCount} selected</Badge>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent>
        <div className="mt-2 border rounded-md p-3 bg-muted/20 space-y-4 max-h-60 overflow-y-auto" data-testid="bundle-attachment-picker">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading available files…
            </div>
          )}

          {!loading && groups && totalAvailable === 0 && (
            <p className="text-sm text-muted-foreground py-1">No additional documents or files available.</p>
          )}

          {!loading && groups && Object.entries(GROUP_LABELS).map(([key, label]) => {
            const items = (groups[key] || []).filter(i => !excludeSet.has(i.file_id));
            if (!items.length) return null;
            return (
              <div key={key} className="space-y-1.5">
                <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{label}</p>
                {items.map(item => (
                  <div key={item.file_id} className="flex items-center gap-2">
                    <Checkbox
                      id={`attach-${item.file_id}`}
                      checked={Boolean(selected[item.file_id])}
                      onCheckedChange={() => toggle(item.file_id)}
                      data-testid={`attach-checkbox-${item.file_id}`}
                    />
                    <Label
                      htmlFor={`attach-${item.file_id}`}
                      className="text-sm leading-tight cursor-pointer flex-1"
                    >
                      {item.label}
                    </Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 w-6 p-0 text-muted-foreground hover:text-foreground shrink-0"
                      onClick={() => openPreview(item.file_id)}
                      title="Preview"
                      type="button"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default BundleAttachmentPicker;
